import {
  IQuoteValidation,
  RuleValidationErrorLevelEnum,
  RuleValidationErrorLocationEnum,
  RuleValidationField,
} from '@monetize/types/app';

export const QUOTE_FIELD_ATTRIBUTE = 'data-quote-field';

/**
 * Marks the element inside an anchor that holds the field's own control, so
 * `focusQuoteField` does not focus something else the anchor happens to wrap
 * (a row's title button, a label tooltip). Optional — anchors without it fall
 * back to searching the whole anchor.
 */
export const QUOTE_FIELD_CONTROL_ATTRIBUTE = 'data-quote-field-control';

export const QUOTE_FIELD_HIGHLIGHT_ATTRIBUTE = 'data-quote-field-highlight';

export const QUOTE_FIELD_HIGHLIGHT_DURATION_MS = 1600;

export const QUOTE_FIELD_ANCHOR_SX = {
  scrollMarginTop: '8rem',
  borderRadius: 'sm',
  transition: 'background-color 0.25s ease-in-out',
  [`&[${QUOTE_FIELD_HIGHLIGHT_ATTRIBUTE}]`]: {
    backgroundColor: 'tBlue.hover',
  },
};

export const QUOTE_VALIDATION_COLOR: {
  [key in RuleValidationErrorLevelEnum]: string;
} = {
  [RuleValidationErrorLevelEnum.ERROR]: 'tRed.base',
  [RuleValidationErrorLevelEnum.WARN]: 'tOrange.tangerine',
  [RuleValidationErrorLevelEnum.INFO]: 'tIndigo.base',
};

/** Rendered as one "Contract Term" row, so all three share a single anchor. */
export const CONTRACT_TERM_FIELD_KEYS = [
  'contractStartDate',
  'contractEndDate',
  'contractLength',
] as const;

/** Rendered as one "Renewal Action" row, so all three share a single anchor. */
export const RENEWAL_ACTION_FIELD_KEYS = [
  'contractRenewalTerms.contractEndAction',
  'contractRenewalTerms.renewalTermLength.months',
  'contractRenewalTerms.autoRenewalNoticePeriod',
] as const;

/**
 * Quote fields that render a validation anchor on the quote page.
 *
 * This is the single source of truth for the feature: the rule builder's field
 * picker is filtered by it (so an admin cannot select a field whose message
 * would have nowhere to render), and the quote page anchors are driven by the
 * same constants. The rules schema exposes ~125 quote fields — most of them
 * (`account.*`, `revenueMetrics.*`, internal `_`-prefixed values) have no
 * corresponding control on the quote page.
 *
 * Entries match as key prefixes, so `customFields` covers every custom field
 * and `contacts.billing` covers every billing-contact subfield.
 *
 * Some of these render conditionally (PO Number, VAT Number, Output Language,
 * Amendment Date...). They stay selectable — the rule is still meaningful on
 * quotes where the field is shown — and `findQuoteFieldAnchor` is what decides
 * at runtime whether the alerts panel offers a "Jump to" link.
 *
 * To add a field: add its key here and pass the matching `fieldKey` to the
 * `QuoteFieldAnchor` (or `QuoteBasicInfoLine`) that renders it. `fieldKey` is
 * typed as `QuoteFieldAnchorKey`, so the two lists cannot drift apart silently.
 */
export const QUOTE_FIELD_ANCHOR_KEYS = [
  'accountId',
  'contacts.billing',
  'contractAmendmentDate',
  'customFields',
  'discountApplicationMethod',
  'expirationDate',
  'netTerms',
  'outputLanguage',
  'owner',
  'purchaseOrderNumber',
  'requiresEsign',
  'vatNumber',
  ...CONTRACT_TERM_FIELD_KEYS,
  ...RENEWAL_ACTION_FIELD_KEYS,
] as const;

export type QuoteFieldAnchorKey = (typeof QUOTE_FIELD_ANCHOR_KEYS)[number];

/**
 * A key an anchor can be mounted for: one of the registry keys, or a specific
 * custom field, which the `customFields` registry entry covers by prefix.
 */
export type QuoteFieldKey = QuoteFieldAnchorKey | `customFields.${string}`;

export const QUOTE_FIELD_ANCHORS: ReadonlySet<string> = new Set(
  QUOTE_FIELD_ANCHOR_KEYS,
);

/**
 * Not escaped: every key reaching this comes from the rules schema, whose quote
 * field keys are code-defined dotted paths, or from a custom field, whose API
 * name is constrained to `/^[a-zA-Z0-9_]*$/` at creation. Nothing here can
 * carry a quote or bracket.
 */
export const getQuoteFieldAnchorSelector = (fieldKey: string) =>
  `[${QUOTE_FIELD_ATTRIBUTE}~="${fieldKey}"]`;

export const getValidationFields = (validation: IQuoteValidation) =>
  validation.errorLocation === RuleValidationErrorLocationEnum.SPECIFIC_FIELDS
    ? (validation.fields ?? [])
    : [];

/**
 * The display name for a stored validation field. Rule-builder labels are
 * namespaced for its hierarchical dropdown (`Contacts -> Billing Email`,
 * `Custom Fields -> Renewal Owner`); only the last segment reads as prose in
 * "Jump to ..." on the quote page.
 */
export const getValidationFieldLabel = ({ label }: RuleValidationField) =>
  label.split('->').at(-1)?.trim() || label;

export const isFieldWithin = (fieldKey: string, rowFieldKey: string) =>
  fieldKey === rowFieldKey || fieldKey.startsWith(`${rowFieldKey}.`);

/**
 * A field key and each of its ancestors, most specific first — so a validation
 * on `contacts.billing.email` can resolve to the `contacts.billing` anchor.
 */
export const getAnchorCandidates = (fieldKey: string) => {
  const parts = fieldKey.split('.');
  return parts.map((_, index) =>
    parts.slice(0, parts.length - index).join('.'),
  );
};

/** Whether the quote page has an anchor that could ever render this field. */
export const isQuoteFieldAnchored = (fieldKey: string) =>
  getAnchorCandidates(fieldKey).some((candidate) =>
    QUOTE_FIELD_ANCHORS.has(candidate),
  );

/**
 * Validations to render under a row, deduped — a row can host several field
 * keys (Contract Term hosts three) and one rule can target more than one of
 * them, but the message should only appear once.
 */
export const getValidationsForFields = (
  validations: IQuoteValidation[],
  rowFieldKeys: readonly string[],
) =>
  validations.filter((validation) =>
    getValidationFields(validation).some(({ key }) =>
      rowFieldKeys.some((rowFieldKey) => isFieldWithin(key, rowFieldKey)),
    ),
  );

/**
 * The closest mounted anchor for a field key, or null when the quote does not
 * currently render it (a setting is off, the quote is not an amendment, ...).
 */
export const findQuoteFieldAnchor = (fieldKey: string) =>
  getAnchorCandidates(fieldKey).reduce<HTMLElement | null>(
    (found, candidate) =>
      found ??
      document.querySelector<HTMLElement>(
        getQuoteFieldAnchorSelector(candidate),
      ),
    null,
  );

/**
 * The control to focus within an anchor. Real form controls win over anything
 * merely focusable: an anchor spans the whole row, and a row whose title opens
 * a drawer renders that button *before* the field, so a flat "first focusable"
 * search would hand focus to the drawer trigger instead of the field.
 */
const findAnchorControl = (anchor: HTMLElement) => {
  const root =
    anchor.querySelector<HTMLElement>(`[${QUOTE_FIELD_CONTROL_ATTRIBUTE}]`) ??
    anchor;
  return (
    root.querySelector<HTMLElement>(
      'input:not([type="hidden"]), textarea, select',
    ) ?? root.querySelector<HTMLElement>('button, [tabindex]')
  );
};

export const focusQuoteField = (fieldKey: string) => {
  const anchor = findQuoteFieldAnchor(fieldKey);
  if (!anchor) {
    return;
  }

  anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });

  anchor.setAttribute(QUOTE_FIELD_HIGHLIGHT_ATTRIBUTE, 'true');
  window.setTimeout(
    () => anchor.removeAttribute(QUOTE_FIELD_HIGHLIGHT_ATTRIBUTE),
    QUOTE_FIELD_HIGHLIGHT_DURATION_MS,
  );

  findAnchorControl(anchor)?.focus({ preventScroll: true });
};
